// Shared bare-URL segmentation: splits a plain-text string into text / url
// runs so callers can render `https://…` as a real link. Used by the
// rehype-bare-autolink plugin (markdown pipeline) and by LinkifiedText
// (plain-text chat bubbles — user turns and WhatsApp group member turns,
// which never go through markdown).

export interface LinkifySegment {
  kind: 'text' | 'url';
  value: string;
}

// A bare URL ends at whitespace OR at a quote or angle bracket — the same token rule as
// `task-known-action.ts`'s URL_TOKEN (and iOS/Android's bare-URL pass). Without the stop set, a JSON
// blob like `{"siteUrl":"https://a.com/","url":"https://a.com/faq"}` (a held card's raw arguments)
// became ONE link `https://a.com/","url":"https://a.com/faq` — a 404. `'` is legal in a URL but
// effectively never appears unencoded in one, while it routinely quotes one in prose and code.
const URL_RE = /https?:\/\/[^\s<>"']+/gi;

function count(str: string, ch: string): number {
  let n = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === ch) n++;
  }
  return n;
}

// GFM-style trailing-punctuation trim. Returns the trimmed URL (which may be
// shorter than the raw match); the caller keeps the stripped remainder as
// plain text. Recomputes bracket balance after each strip.
export function trimTrailingPunctuation(url: string): string {
  let end = url.length;
  // Loop: repeatedly inspect the last kept char and decide whether to drop it.
  for (;;) {
    if (end === 0) break;
    const last = url[end - 1];
    if (last === '.' || last === ',' || last === ';' || last === ':' || last === '!' || last === '?' || last === "'" || last === '"') {
      end--;
      continue;
    }
    const candidate = url.slice(0, end);
    if (last === ')') {
      if (count(candidate, ')') > count(candidate, '(')) {
        end--;
        continue;
      }
      break;
    }
    if (last === ']') {
      if (count(candidate, ']') > count(candidate, '[')) {
        end--;
        continue;
      }
      break;
    }
    if (last === '}') {
      if (count(candidate, '}') > count(candidate, '{')) {
        end--;
        continue;
      }
      break;
    }
    break;
  }
  return url.slice(0, end);
}

// Split `value` into ordered text/url segments. A string with no linkifiable
// URL comes back as a single text segment equal to the input.
export function linkifySegments(value: string): LinkifySegment[] {
  URL_RE.lastIndex = 0;
  const out: LinkifySegment[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_RE.exec(value)) !== null) {
    const raw = match[0];
    const trimmed = trimTrailingPunctuation(raw);
    // Require a real URL after trimming: scheme + at least one more char.
    if (!/^https?:\/\/.+/i.test(trimmed)) {
      continue;
    }
    const start = match.index;
    if (start > cursor) {
      out.push({ kind: 'text', value: value.slice(cursor, start) });
    }
    out.push({ kind: 'url', value: trimmed });
    cursor = start + trimmed.length;
    // Continue scanning after the trimmed URL so stripped trailing punctuation
    // (e.g. a `.` or `)`) is preserved as plain text.
    URL_RE.lastIndex = cursor;
  }
  if (out.length === 0) return [{ kind: 'text', value }];
  if (cursor < value.length) {
    out.push({ kind: 'text', value: value.slice(cursor) });
  }
  return out;
}
